/** @format */

import { useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { PostMethod } from "../../api/Api";
import { itemValidators } from "../../validators/validationPattern";
import {
  getSession,
  setBankAccountInfoData,
  setEntryCheckInfoData,
  setMerchantInfoData,
  setOperationInfoData,
  setRepresentativeInfoData,
  setShopInfoData,
  setUserInfoData,
} from "../../sessionStorage/sessionStorageMethod";
import { Context } from "../../stores/Provider";
import {
  bankNameBranchValidator,
  bankNameValidator,
  blankValidate,
  comparePriceValidator,
  fileValidator,
} from "../../validators/publicValidator";
import {
  setIsLoading,
  logoutAbnormalFunction,
  useNavgatePath,
  removeSpaces,
  noValidateMessage,
  fullWidthToHalfWidth,
  toAnnualBusiness,
} from "../../function";
import {
  entryInfoPostRequestDataType,
  initApiModelType,
  entryInfoPostResponseDataType,
  initBankAccountSessionDataType,
  initReviewContentsDivisionSessionDataType,
  initShopSessionDataType,
  initMerchantSessionDataType,
  initRepresentativeSessionDataType,
} from "../../constance/typeModel";
import { initShopSessionData } from "../../constance/sessionData";
import { initApiResponse } from "../../constance/constance";
import {
  validateMessageBankInsertDataType,
  validateMessageMerchantInsertDataType,
  validateMessageShopInsertDataType,
} from "../../constance/providerTypeModel";

const useEmochiEntryAPIHooks = (navigateByAgent: Function, redirect?: boolean) => {
  const { state, dispatch } = useContext(Context);
  const location = useLocation();
  const userInfoData = getSession("user");
  const agentMasterInfoData = getSession("agent_master");
  const merchantSessionData = getSession("merchant");
  const entryCheckInfoData = getSession("entry_check");
  const economicInfoData = getSession("economic_condition");
  const merchantInfoData = state.entry.merchant;
  const bankAccountInfoData = state.entry.bank_account;
  const representativeInfoData = state.entry.representative;
  const operationInfoData = state.entry.operation;
  const fileInfoData = state.entry.files;
  const shopInfoData = state.entry.shop;
  const ObjToDevide = state.entry.review_contents_division;
  const ipFlag = userInfoData.ip_flag;

  // 申込画面のパス
  const path = useMemo(() => {
    const pathArray = location.pathname.split("/");
    return pathArray[pathArray.length - 1];
  }, [location.pathname]);

  const { navigateTo } = useNavgatePath(path, redirect);

  // 確認画面からの遷移か確認するフラグ
  const fromConfirmFlag = useMemo(() => location.state?.confirm_flag ?? false, [location.state]);

  // 選択しているコンテンツに紐づく項目のみ値をセットし、紐づいていない項目にはnullをセットする
  const setReviewContentsNullHandler = (
    shopData: initShopSessionDataType,
    deivdeData: initReviewContentsDivisionSessionDataType,
    merchantData: { merchant_type: string },
  ) => {
    const setNameObj = {
      antique_flag: ["shop_addition_antique_flag"],
      btob_flag: ["shop_addition_btob_code"],
      estimate_flag: ["shop_addition_estimated_product_flag"],
      online_local_flag: ["shop_addition_online", "shop_addition_offline"],
      prepaid_continuous_flag: ["shop_addition_prepaid_flag"],
      purchase_points_flag: ["shop_addition_point_purchase_flag"],
      real_store_flag: [
        "shop_addition_physical_store_flag",
        "shop_addition_physical_store_url_flag",
        "shop_addition_physical_store_url",
      ],
      rental_flag: ["shop_addition_rental_flag", "shop_addition_rental_product_url"],
      reserve_item_flag: ["shop_addition_reserved_product_flag", "shop_addition_reservation_period_flag"],
      subscription_flag: ["shop_addition_subscription_flag", "shop_addition_subscription_code"],
    } as const;
    let resultObj: { [key: string]: string | number | boolean | null } = {};
    Object.entries(setNameObj).forEach(([flagName, shopItemArray]) => {
      let shouldSetValues: boolean;
      if (flagName === "real_store_flag") {
        // real_store_flagは特別条件で判定
        shouldSetValues = !!deivdeData.real_store_flag && merchantData.merchant_type === "2";
      } else {
        // その他のフラグは通常の判定
        shouldSetValues = !!deivdeData[flagName];
      }
      shopItemArray.forEach((itemName: string) => {
        resultObj[itemName] = shouldSetValues ? shopData[itemName] : null;
      });
    });
    resultObj.shop_addition_license_flag = deivdeData.license_flag;
    return resultObj;
  };

  // 店舗情報をAPIにリクエストするために整形
  const merchantInsert = useMemo((): initMerchantSessionDataType => {
    const merchantPostCode: string =
      merchantInfoData.merchant_register_post_code3 !== "" && merchantInfoData.merchant_register_post_code4 !== ""
        ? merchantInfoData.merchant_register_post_code3 + "-" + merchantInfoData.merchant_register_post_code4
        : "";
    const merchantWebsiteUrl = "";
    const formattedSessionData: initMerchantSessionDataType = {
      ...merchantInfoData,
      merchant_register_post_code: merchantPostCode,
      merchant_annual_business: toAnnualBusiness(merchantInfoData.merchant_annual_business),
      merchant_website_url: merchantWebsiteUrl,
    };
    return formattedSessionData;
  }, [merchantInfoData]);

  // 代表者情報をAPIにリクエストするために整形
  const representativeInsert = useMemo((): initRepresentativeSessionDataType => {
    let representationBirthday =
      representativeInfoData.representative_birthday_CE +
      "-" +
      representativeInfoData.representative_birthday_month +
      "-" +
      representativeInfoData.representative_birthday_day;
    representationBirthday = representationBirthday === "--" ? "" : representationBirthday;
    const formattedSessionData: initRepresentativeSessionDataType = {
      ...representativeInfoData,
      representative_birthday: representationBirthday,
    };
    return formattedSessionData;
  }, [representativeInfoData]);

  // ショップ情報をAPIにリクエストするために整形
  const shopInsert = useMemo(() => {
    const shopNameAlphabet =
      shopInfoData.shop_name_alphabet !== null ? fullWidthToHalfWidth(shopInfoData.shop_name_alphabet) : null;
    const deivdeItemObj = setReviewContentsNullHandler(shopInfoData, ObjToDevide, merchantSessionData);
    const shopNameAtobarai = economicInfoData.atobarai.system_setting.select_flag
      ? shopInfoData.shop_atobarai_customer_notice_store_name !== ""
        ? shopInfoData.shop_atobarai_customer_notice_store_name.replace(/‐/g, "－")
        : ""
      : "";
    const formattedSessionData = {
      ...shopInfoData,
      ...deivdeItemObj,
      shop_name_alphabet: shopNameAlphabet,
      shop_atobarai_customer_notice_store_name: shopNameAtobarai,
      shop_cvs_website_name: "",
      shop_cvs_website_name_kana: "",
      shop_addition_price_range_max: null,
      shop_addition_price_range_min: null,
    };
    return formattedSessionData;
  }, [shopInfoData, merchantSessionData, ObjToDevide]);

  // ショップ情報をAPIにリクエストするために整形（既存IPあり）
  const ipDependentShopInsert = useMemo(() => {
    const shopNameAlphabet =
      shopInfoData.shop_name_alphabet !== null ? fullWidthToHalfWidth(shopInfoData.shop_name_alphabet) : null;
    const deivdeItemObj = setReviewContentsNullHandler(shopInfoData, ObjToDevide, merchantSessionData);
    const shopInsert = {
      ...shopInfoData,
      ...deivdeItemObj,
      shop_name_alphabet: shopNameAlphabet,
    };
    return shopInsert;
  }, [shopInfoData, merchantSessionData, ObjToDevide]);

  // 既存IPがある場合に加盟店情報に挿入するデータ
  const ipDependentMerchantData = useMemo(() => {
    return {
      merchant_annual_business: null,
    };
  }, []);

  // 既存IPがあるかつ個人の場合に加盟店情報に挿入するデータ
  const ipDependentPersonalMerchantData = useMemo(() => {
    return {
      representative_sex: null,
      representative_birthday: null,
    };
  }, []);

  // 既存IPがある場合に口座情報に挿入するデータ
  const ipDependentBankData = useMemo(() => {
    return {
      bank_account_category: null,
    };
  }, []);

  // 申し込みAPI送信用データをまとめる
  const setMainData = () => {
    const { ip_flag } = userInfoData;
    let mainData = {
      ...userInfoData,
      status: state.user_status.status,
    };
    switch (path) {
      case "merchant": {
        const preInsertMerchantData = {
          ...merchantInsert,
          ...representativeInsert,
          ...operationInfoData,
          ...(ip_flag ? ipDependentMerchantData : {}),
          ...(ip_flag && merchantInfoData.merchant_type === "2" ? ipDependentPersonalMerchantData : {}),
        };
        mainData.merchant = {
          merchant: removeSpaces<initMerchantSessionDataType>(preInsertMerchantData),
        };
        break;
      }
      case "bank_account": {
        let preInsertBankData = {
          ...bankAccountInfoData,
          ...(ip_flag ? ipDependentBankData : {}),
        };
        mainData.merchant = {
          bank: removeSpaces<initBankAccountSessionDataType>(preInsertBankData),
        };
        break;
      }
      case "shop": {
        let preInsertShopData = {
          ...initShopSessionData,
          ...(ip_flag ? ipDependentShopInsert : shopInsert),
        };
        mainData.shop = removeSpaces<initShopSessionDataType>(preInsertShopData);
        break;
      }
      default:
        return mainData;
    }
    return mainData;
  };

  // ======================================================
  // レスポンスデータの整形
  // 加盟店レスポンスデータをセッションにセットするために整形
  const merchantResponseDataMolder = (responseData: entryInfoPostResponseDataType) => {
    let merchantObj = {};
    let representativeObj = {};
    let operatorObj = {};
    let representative_birthday_CE = "";
    let representative_birthday_month = "";
    let representative_birthday_day = "";
    for (let merchantItem in responseData.merchant) {
      if (merchantItem.split("_")[0] === "merchant") {
        merchantObj = { ...merchantObj, [merchantItem]: responseData.merchant[merchantItem] };
      }
      if (merchantItem.split("_")[0] === "representative") {
        if (
          merchantItem === "representative_birthday" &&
          responseData.merchant.representative_birthday !== "" &&
          responseData.merchant.representative_birthday !== null
        ) {
          representative_birthday_CE = responseData.merchant.representative_birthday.split("-")[0];
          representative_birthday_month = responseData.merchant.representative_birthday.split("-")[1];
          representative_birthday_day = responseData.merchant.representative_birthday.split("-")[2];
        }
        representativeObj = {
          ...representativeObj,
          [merchantItem]: responseData.merchant[merchantItem],
          representative_birthday_CE: representative_birthday_CE,
          representative_birthday_month: representative_birthday_month,
          representative_birthday_day: representative_birthday_day,
        };
      }
      if (merchantItem.split("_")[0] === "operator" || merchantItem.split("_")[0] === "support") {
        operatorObj = { ...operatorObj, [merchantItem]: responseData.merchant[merchantItem] };
      }
    }
    return { merchantObj, representativeObj, operatorObj };
  };

  // 銀行レスポンスデータをセッションにセットするために整形
  const bankResponseDataMolder = (responseData: entryInfoPostResponseDataType) => {
    let bankObj = {};
    for (let merchantItem in responseData.merchant) {
      if (merchantItem.split("_")[0] === "bank") {
        bankObj = { ...bankObj, [merchantItem]: responseData.merchant[merchantItem] };
      }
    }
    return { bankObj };
  };

  // 申込レスポンスデータを加盟店、銀行、ショップ情報にセット
  const setResponseDataHandler = (response: initApiModelType<entryInfoPostResponseDataType>) => {
    const responseData = response.response_data;
    if (responseData === undefined) return;
    switch (path) {
      case "merchant": {
        const { merchantObj, representativeObj, operatorObj } = merchantResponseDataMolder(responseData);
        setMerchantInfoData({ ...merchantInfoData, ...merchantObj });
        setRepresentativeInfoData({ ...representativeInfoData, ...representativeObj });
        setOperationInfoData({ ...operationInfoData, ...operatorObj });
        dispatch({
          type: "set-entry-data",
          payload: {
            merchant: { ...merchantInfoData, ...merchantObj },
            representative: { ...representativeInfoData, ...representativeObj },
            operation: { ...operationInfoData, ...operatorObj },
          },
        });
        break;
      }
      case "bank_account": {
        const { bankObj } = bankResponseDataMolder(responseData);
        setBankAccountInfoData({ ...bankAccountInfoData, ...bankObj });
        dispatch({
          type: "set-entry-bank-account",
          payload: { ...bankAccountInfoData, ...bankObj },
        });
        break;
      }
      case "shop":
        setShopInfoData({ ...shopInfoData, ...responseData.shop });
        dispatch({
          type: "set-entry-shop",
          payload: { ...shopInfoData, ...responseData.shop },
        });
        break;
      default:
        return;
    }
    const insertUserData = { ...userInfoData };
    insertUserData.entry_id = responseData.entry_id;
    setUserInfoData(insertUserData);
  };

  // ======================================================
  // バリデーションの実行
  // お客様情報のバリデーションを実施
  const merchantValidator = () => {
    const merchantValidateData = state.validateMessages.merchant;
    let validateData: validateMessageMerchantInsertDataType = {};
    const merchantArray = [
      "merchant_name",
      "merchant_name",
      "merchant_name_kana",
      "merchant_last_name",
      "merchant_first_name",
      "merchant_last_name_kana",
      "merchant_first_name_kana",
      "merchant_phone_number_before",
      "merchant_phone_number_middle",
      "merchant_phone_number_after",
      "merchant_register_post_code3",
      "merchant_register_post_code4",
      "merchant_address_municipality",
      "merchant_address_municipality_kana",
      "merchant_address_streetbunch",
      "merchant_address_streetbunch_kana",
      "merchant_address_building",
      "merchant_address_building_kana",
      "merchant_website_url",
      "merchant_annual_business",
      "merchant_shop_category",
      "merchant_corporate_number",
    ];
    const representativeArray = [
      "representative_last_name",
      "representative_first_name",
      "representative_last_name_kana",
      "representative_first_name_kana",
    ];
    const oparatorArray = [
      "operator_last_name",
      "operator_first_name",
      "operator_last_name_kana",
      "operator_first_name_kana",
      "operator_email",
      "operator_phone_number_before",
      "operator_phone_number_middle",
      "operator_phone_number_after",
      "support_operator_email",
      "support_operator_phone_number_before",
      "support_operator_phone_number_middle",
      "support_operator_phone_number_after",
    ];
    merchantArray.forEach((item) => {
      validateData[item] = itemValidators<string | number | null>("merchant", item, merchantInfoData[item]);
    });
    representativeArray.forEach((item) => {
      validateData[item] = itemValidators<string | null>("merchant", item, representativeInfoData[item]);
    });
    oparatorArray.forEach((item) => {
      validateData[item] = itemValidators<string>("merchant", item, operationInfoData[item]);
    });
    validateData.identity_doc_images = fileValidator(fileInfoData.identity_doc_images);
    const validateResult = noValidateMessage<validateMessageMerchantInsertDataType>(validateData, merchantValidateData);
    dispatch({
      type: "set-validate-merchant",
      payload: {
        ...validateResult,
      },
    });
    return validateResult;
  };

  // 口座情報のバリデーションを実施
  const bankAccountValidator = () => {
    const bankAccountValidateData = state.validateMessages.bank_account;
    const bankArray = ["bank_account_number", "bank_account_name"];
    let validateData: validateMessageBankInsertDataType = {};
    bankArray.forEach((item) => {
      validateData[item] = itemValidators<string | number | null>("bank_account", item, bankAccountInfoData[item]);
    });
    validateData.bank_account_bank_name = bankNameValidator(
      bankAccountInfoData.bank_account_bank_name,
      state.entry_bank_pulldown,
    );
    validateData.bank_account_bank_branch = bankNameBranchValidator(bankAccountInfoData, state.entry_bank_pulldown);
    validateData.passbook_images = fileValidator(fileInfoData.passbook_images);
    const validateResult = noValidateMessage<validateMessageBankInsertDataType>(validateData, bankAccountValidateData);
    dispatch({
      type: "set-validate-bank_account",
      payload: {
        ...validateResult,
      },
    });
    return validateResult;
  };

  // ショップ情報のバリデーションを実施
  const shopValidator = () => {
    const shopValidateData = state.validateMessages.shop;
    const shopArray = [
      "account_id",
      "shop_name",
      "shop_name_kana",
      "shop_name_alphabet",
      "shop_addition_shop_website_check_url",
      "shop_addition_business_deal_url",
      "shop_cvs_website_name",
      "shop_cvs_website_name_kana",
      "shop_website_url",
      "shop_addition_price_range_min",
      "shop_addition_price_range_max",
      "shop_addition_product_content_freetext",
      "shop_addition_rental_product_url",
      "shop_addition_physical_store_url",
      "shop_addition_business_deal_url",
      "shop_addition_subscription_code",
      "shop_addition_subscription_flag",
      "shop_atobarai_customer_notice_store_name",
      "shop_addition_shop_website_password",
      "shop_addition_shop_website_id",
    ];
    const fileArray = ["antique", "examination_document", "rental", "license"];
    let validateData: validateMessageShopInsertDataType = {};
    let subscription_validate = { shop_addition_subscription_flag: { isValidate: false, message: "" } };
    const entryInfoSubscription = economicInfoData.credit.system_setting.subscription;
    shopArray.forEach((item) => {
      if (item === "shop_addition_subscription_flag") {
        if (entryInfoSubscription === false && shopInfoData[item] === true) {
          subscription_validate = {
            shop_addition_subscription_flag: {
              isValidate: true,
              message: "サブスク課金の有無を「あり」にしている場合は、自動継続課金オプションを選択してください。",
            },
          };
        } else if (entryInfoSubscription === true && shopInfoData[item] === false) {
          subscription_validate = {
            shop_addition_subscription_flag: {
              isValidate: true,
              message: "自動継続課金オプションを選択している場合は、サブスク課金の有無を「あり」にしてください。",
            },
          };
        } else {
          subscription_validate = { shop_addition_subscription_flag: { isValidate: false, message: "" } };
        }
      } else {
        validateData[item] = itemValidators<string | number | boolean | null>("shop", item, shopInfoData[item]);
      }
    });
    fileArray.forEach((item) => {
      validateData[item] = fileValidator(fileInfoData[item]);
    });
    validateData.shop_addition_price_validate = comparePriceValidator(shopInfoData);
    const validateObject = noValidateMessage<validateMessageShopInsertDataType>(validateData, shopValidateData);
    const validateResult = { ...validateObject, ...subscription_validate };
    dispatch({
      type: "set-validate-shop",
      payload: {
        ...validateResult,
      },
    });
    return validateResult;
  };

  //項目ごとにバリデーションを実施
  const pageValidator = <T,>(page: T) => {
    const devision = page !== null ? page : path;
    switch (devision) {
      case "merchant":
        return merchantValidator();
      case "bank_account":
        return bankAccountValidator();
      case "shop":
        return shopValidator();
      default:
        return {};
    }
  };

  // お客様情報まとめる（空欄バリデーション用）
  const arrangeMerchantData = useMemo(() => {
    const unionData = {
      ...merchantInsert,
      ...representativeInsert,
      ...operationInfoData,
      identity_doc_images: fileInfoData.identity_doc_images,
    };
    return unionData;
  }, [merchantInsert, representativeInsert, operationInfoData, fileInfoData]);

  // 口座情報まとめる（空欄バリデーション用）
  const arrangeBankAccountData = useMemo(() => {
    const unionData = {
      ...bankAccountInfoData,
      passbook_images: fileInfoData.passbook_images,
    };
    return unionData;
  }, [bankAccountInfoData, fileInfoData]);

  // ショップ情報まとめる（空欄バリデーション用）
  const arrangeShopData = useMemo(() => {
    const unionData = {
      ...shopInfoData,
      antique: fileInfoData.antique,
      license: fileInfoData.license,
      examination_document: fileInfoData.examination_document,
    };
    return unionData;
  }, [shopInfoData, fileInfoData]);

  // ページごとに申込データを変更（空欄バリデーション用）
  const devideDataByPages = (page: string) => {
    switch (page) {
      case "merchant":
        return arrangeMerchantData;
      case "bank_account":
        return arrangeBankAccountData;
      case "shop":
        return arrangeShopData;
      default:
        return {};
    }
  };

  // 空欄バリデーション
  const blankValidator = <T extends string | null>(
    validateData:
      | validateMessageMerchantInsertDataType
      | validateMessageBankInsertDataType
      | validateMessageShopInsertDataType,
    page: T,
  ) => {
    const devision = page !== null ? page : path;
    const recoilByPages = devideDataByPages(devision);
    const { blankData } = blankValidate(recoilByPages, validateData, devision);
    dispatch({
      type: `set-validate-${devision}`,
      payload: {
        ...blankData,
      },
    });
    return { blankData };
  };

  // バリデーション成功判定(Bカート用)
  const validateSuccessJudge = <T,>(
    validateData:
      | validateMessageMerchantInsertDataType
      | validateMessageBankInsertDataType
      | validateMessageShopInsertDataType,
    page: T,
  ) => {
    let validateFlag = false;
    const devision = page !== null ? page : path;
    // 対象外の項目はバリデーションを無視
    let checkData: string[] = [];
    const merchantIpDeleteMerchantData = [
      "merchant_phone_number_before",
      "merchant_phone_number_middle",
      "merchant_phone_number_after",
      "merchant_register_post_code3",
      "merchant_register_post_code4",
      "merchant_address_prefectures",
      "merchant_address_municipality",
      "merchant_address_streetbunch",
      "merchant_address_municipality_kana",
      "merchant_address_streetbunch_kana",
      "merchant_annual_business",
      "merchant_shop_category",
      "support_operator_email",
      "support_operator_phone_number_before",
      "support_operator_phone_number_middle",
      "support_operator_phone_number_after",
      "identity_doc_images",
    ];
    const personalIpDeleteMerchantData = [
      "merchant_phone_number_before",
      "merchant_phone_number_middle",
      "merchant_phone_number_after",
      "merchant_register_post_code3",
      "merchant_register_post_code4",
      "merchant_address_prefectures",
      "merchant_address_municipality",
      "merchant_address_streetbunch",
      "merchant_address_municipality_kana",
      "merchant_address_streetbunch_kana",
      "merchant_annual_business",
      "merchant_shop_category",
      "representative_sex",
      "representative_birthday_CE",
      "representative_birthday_month",
      "representative_birthday_day",
      "support_operator_email",
      "support_operator_phone_number_before",
      "support_operator_phone_number_middle",
      "support_operator_phone_number_after",
    ];
    const ipDeleteBankData = [
      "passbook_images",
      "bank_account_bank_name",
      "bank_account_bank_branch",
      "bank_account_category",
      "bank_account_number",
      "bank_account_name",
    ];
    const ipDeleteShopData = [
      "shop_name",
      "shop_name_kana",
      "shop_name_alphabet",
      "shop_addition_physical_store_flag",
      "shop_addition_physical_store_url_flag",
      "shop_addition_physical_store_url",
    ];

    if (devision === "merchant") {
      checkData.push(
        "merchant_type",
        "merchant_phone_number_before",
        "merchant_phone_number_middle",
        "merchant_phone_number_after",
        "merchant_register_post_code3",
        "merchant_register_post_code4",
        "merchant_address_prefectures",
        "merchant_address_municipality",
        "merchant_address_municipality_kana",
        "merchant_address_streetbunch",
        "merchant_address_streetbunch_kana",
        "merchant_address_building",
        "merchant_address_building_kana",
        "merchant_annual_business",
        "merchant_shop_category",
        "operator_last_name",
        "operator_last_name_kana",
        "operator_first_name",
        "operator_first_name_kana",
        "operator_email",
        "operator_phone_number_before",
        "operator_phone_number_middle",
        "operator_phone_number_after",
        "support_operator_email",
        "support_operator_phone_number_before",
        "support_operator_phone_number_middle",
        "support_operator_phone_number_after",
      );
      // Bカートは法人のみ？
      if (merchantInfoData.merchant_type === "1") {
        checkData.push(
          "merchant_name",
          "merchant_name_kana",
          "merchant_corporate_number",
          "representative_last_name",
          "representative_first_name",
          "representative_last_name_kana",
          "representative_first_name_kana",
          "representative_sex",
          "representative_birthday_CE",
          "representative_birthday_month",
          "representative_birthday_day",
        );
      } else if (merchantInfoData.merchant_type === "2") {
        checkData.push(
          "identity_doc_images",
          "merchant_last_name",
          "merchant_first_name",
          "merchant_last_name_kana",
          "merchant_first_name_kana",
          "representative_sex",
          "representative_birthday_CE",
          "representative_birthday_month",
          "representative_birthday_day",
        );
      }
    }
    if (devision === "bank_account") {
      checkData.push(
        "passbook_images",
        "bank_account_bank_name",
        "bank_account_bank_branch",
        "bank_account_category",
        "bank_account_number",
        "bank_account_name",
      );
    }
    if (devision === "shop") {
      checkData.push(
        "shop_name",
        "shop_name_kana",
        "shop_name_alphabet",
        "shop_website_url",
        "shop_addition_product_content_code",
        "shop_addition_major_content_code",
        "shop_addition_medium_content_code",
        "shop_addition_product_content_freetext",
        "shop_addition_available_product_flag",
        "shop_addition_non_infringing_product_presence_absence_flag",
        "shop_addition_non_administrative_penalties_five_year_flag",
        "shop_addition_non_losing_judgment_flag",
      );

      // BカートはアカウントIDを画面に表示しない？
      if (agentMasterInfoData.account_id_flag) {
        checkData.push("account_id");
      }

      if (shopInfoData.shop_addition_site_status_code === 0) {
        checkData.push("shop_addition_site_status_method");
        if (shopInfoData.shop_addition_site_status_method === 0) {
          checkData.push("shop_addition_business_deal_url", "shop_addition_shop_website_check_url");
        } else if (shopInfoData.shop_addition_site_status_method === 1) {
          checkData.push("examination_document");
        }
      }
      if (economicInfoData.atobarai.system_setting.select_flag) {
        checkData.push("shop_atobarai_customer_notice_store_name");
      }
      if (ObjToDevide.antique_flag) {
        checkData.push("shop_addition_antique_flag");
        if (shopInfoData.shop_addition_antique_flag) {
          checkData.push("antique");
        }
      }
      if (ObjToDevide.subscription_flag) {
        checkData.push("shop_addition_subscription_flag");
        if (shopInfoData.shop_addition_subscription_flag) {
          checkData.push("shop_addition_subscription_code");
        }
      }
      if (ObjToDevide.purchase_points_flag) {
        checkData.push("shop_addition_point_purchase_flag");
      }
      if (ObjToDevide.prepaid_continuous_flag) {
        checkData.push("shop_addition_prepaid_flag");
      }
      if (ObjToDevide.reserve_item_flag) {
        checkData.push("shop_addition_reserved_product_flag");
        if (shopInfoData.shop_addition_reserved_product_flag) {
          checkData.push("shop_addition_reservation_period_flag");
        }
      }
      if (ObjToDevide.estimate_flag) {
        checkData.push("shop_addition_estimated_product_flag");
      }
      if (ObjToDevide.rental_flag) {
        checkData.push("shop_addition_rental_flag");
        if (shopInfoData.shop_addition_rental_flag) {
          // shop_addition_rental_product_urlはAPIHooksのみ（EntrySideHooksには追加なし）
          checkData.push("shop_addition_rental_validate", "shop_addition_rental_product_url");
        }
      }
      if (ObjToDevide.online_local_flag) {
        checkData.push("shop_addition_offer_place");
      }
      if (ObjToDevide.btob_flag) {
        checkData.push("shop_addition_btob_code");
      }
      if (ObjToDevide.license_flag) {
        checkData.push("license");
      }
    }
    // IPあり・なしで制御すること
    if (userInfoData.ip_flag) {
      if (merchantInfoData.merchant_type === "1") {
        checkData = checkData.filter((key) => {
          return !merchantIpDeleteMerchantData.includes(key);
        });
      } else if (merchantInfoData.merchant_type === "2") {
        checkData = checkData.filter((key) => {
          return !personalIpDeleteMerchantData.includes(key);
        });
      }
      checkData = checkData.filter((key) => {
        return !ipDeleteBankData.includes(key);
      });
      checkData = checkData.filter((key) => {
        return !ipDeleteShopData.includes(key);
      });
    }
    // 全ての要素が条件を満たす場合に true を返す
    checkData.forEach((key) => {
      if (validateData[key]!.message !== "") validateFlag = true;
    });
    return validateFlag;
  };

  // バリデーションをかけて、結果をモーダルメッセージを表示
  const validateAndSetMessageHandler = (): { merchant: boolean; bank_account: boolean; shop: boolean } => {
    const pagesArray = ["merchant", "bank_account", "shop"];
    let validateSuccessObj = {
      merchant: false,
      bank_account: false,
      shop: false,
    };
    pagesArray.forEach((page) => {
      let validateData = pageValidator<string>(page);
      const { blankData } = blankValidator<string>(validateData, page);
      if (!validateSuccessJudge<string>(blankData, page)) validateSuccessObj = { ...validateSuccessObj, [page]: true };
    });
    const errorLabels = {
      shop: "ショップ情報",
      bank_account: "口座情報",
      merchant: "お客様情報",
    };
    const failedItems = Object.entries(validateSuccessObj)
      .filter(([key, success]) => !success)
      .map(([key]) => errorLabels[key as keyof typeof errorLabels]);
    if (failedItems.length > 0) {
      const message = `${failedItems.join("・")}に入力エラーがあります。`;
      dispatch({
        type: "set-modal-flag",
        payload: { path, type: "error", flag: true, message },
      });
      // 一定時間後にモーダルを閉じる
      setTimeout(() => {
        dispatch({
          type: "set-modal-flag",
          payload: {
            path: path,
            type: "error",
            flag: false,
            message: "",
          },
        });
      }, 8000);
    }
    return validateSuccessObj;
  };

  // API実行時のモーダル表示ハンドラ
  const setModalFlagHandler = (messageType: string, path: string, type: string) => {
    const messages: Record<string, Record<string, Record<string, string>>> = {
      entry: {
        merchant: {
          success: "お客様情報を保存しました",
          error: "お客様情報の保存に失敗しました",
        },
        bank_account: {
          success: "口座情報を保存しました",
          error: "口座情報の保存に失敗しました",
        },
        shop: {
          success: "ショップ情報を保存しました",
          error: "ショップ情報の保存に失敗しました",
        },
      },
      validate: {
        merchant: {
          default: "お客様情報に入力エラーがあります。",
        },
        bank_account: {
          default: "口座情報に入力エラーがあります。",
        },
        shop: {
          default: "ショップ情報に入力エラーがあります。",
        },
      },
    };
    let message = "";
    if (messageType === "entry") {
      message = messages.entry?.[path]?.[type] || "";
    } else if (messageType === "validate") {
      message = messages.validate?.[path]?.default || "";
    }
    dispatch({
      type: "set-modal-flag",
      payload: {
        path,
        type,
        flag: true,
        message,
      },
    });

    // 一定時間後にモーダルを閉じる
    setTimeout(() => {
      dispatch({
        type: "set-modal-flag",
        payload: {
          path: path,
          type: type,
          flag: false,
          message: "",
        },
      });
    }, 8000);
  };

  //申し込み完了チェックマーク
  const setEntryCheckHandler = (path: string, status: string) => {
    if (path === "shop") {
      setEntryCheckInfoData({ ...entryCheckInfoData, merchant: status, bank_account: status, shop: status });
    } else {
      setEntryCheckInfoData({ ...entryCheckInfoData, [path]: status });
    }
  };

  // ======================================================
  // APIにリクエストするための処理
  // エラーメッセージの削除
  const deleteValidateMassage = () => {
    dispatch({
      type: "set-entry-validate",
      payload: {
        entryValidateMessage: "",
      },
    });
  };

  // 一次保存処理
  const EntryTemporaryPostMethod = async () => {
    let response: initApiModelType<entryInfoPostResponseDataType> = initApiResponse;
    deleteValidateMassage();
    let itemValidateData = pageValidator<null>(null);
    if (validateSuccessJudge<null>(itemValidateData, null)) {
      return setModalFlagHandler("validate", path, "error");
    }
    const mainData = setMainData();
    setIsLoading(true, dispatch);
    response = await PostMethod<entryInfoPostRequestDataType, entryInfoPostResponseDataType>(mainData, "/entry_info");
    if (response.code !== 200) {
      if (response.code === 401) {
        logoutAbnormalFunction(response, dispatch, navigateByAgent, state.agentPath, state.zAccount);
      } else {
        setModalFlagHandler("entry", path, "error");
      }
      return setIsLoading(false, dispatch);
    }
    setModalFlagHandler("entry", path, "success");
    setResponseDataHandler(response);
    setIsLoading(false, dispatch);
  };

  // 本保存処理
  const EntryPostMethod = async () => {
    let response: initApiModelType<entryInfoPostResponseDataType> = initApiResponse;
    deleteValidateMassage();
    // ショップ画面の時、全画面のバリデーションをかけ,それ以外は該当画面のみバリデーションを実行
    if (path === "shop") {
      let validateSuccessObj = validateAndSetMessageHandler();
      if (!validateSuccessObj.merchant || !validateSuccessObj.bank_account || !validateSuccessObj.shop) {
        return;
      }
    } else {
      let validateData = pageValidator<null>(null);
      const { blankData } = blankValidator<null>(validateData, null);
      if (validateSuccessJudge<null>(blankData, null)) {
        return setModalFlagHandler("validate", path, "error");
      }
    }
    const mainData = setMainData();
    setIsLoading(true, dispatch);
    response = await PostMethod<entryInfoPostRequestDataType, entryInfoPostResponseDataType>(mainData, "/entry_info");
    if (response.code !== 200) {
      if (response.code === 401) {
        logoutAbnormalFunction(response, dispatch, navigateByAgent, state.agentPath, state.zAccount);
      } else {
        setModalFlagHandler("entry", path, "error");
      }
      return setIsLoading(false, dispatch);
    }
    setModalFlagHandler("entry", path, "success");
    setEntryCheckHandler(path, "checked");
    setResponseDataHandler(response);
    setIsLoading(false, dispatch);
    window.scrollTo(0, 0);
    return navigateByAgent(navigateTo);
  };

  return { EntryPostMethod, EntryTemporaryPostMethod, fromConfirmFlag, path, ipFlag };
};
export default useEmochiEntryAPIHooks;
